import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  List,
  ListItem,
  ListItemText,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormControl,
  FormLabel,
  Checkbox,
  FormHelperText,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import CancelIcon from "@mui/icons-material/Cancel";
import DownloadIcon from "@mui/icons-material/Download";
import { orderService } from "../services/orderService";
import { format } from "date-fns";
import { toast } from "react-toastify";
import { ManualBankTransferInstructions } from "../components/checkout/PaymentStep";
import { getStatusChipColor } from "../utils/statusUtils";

// Helper function to copy text
const copyToClipboard = (text, feedbackMessage) => {
  navigator.clipboard.writeText(text).then(
    () => {
      toast.success(feedbackMessage || "Copied to clipboard!");
    },
    (err) => {
      toast.error("Failed to copy!");
      console.error("Could not copy text: ", err);
    }
  );
};

const OrderDetailPage = () => {
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation("orders");

  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null); // For user actions
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [returnReason, setReturnReason] = useState("");

  // New state for return feature
  const [returnType, setReturnType] = useState("full");
  const [selectedItems, setSelectedItems] = useState([]);
  const [returnItems, setReturnItems] = useState([]);
  const [itemQuantities, setItemQuantities] = useState({});
  const [itemReasons, setItemReasons] = useState({});
  const [validationError, setValidationError] = useState("");

  // Fetch order details - using useCallback for stability
  const fetchOrder = useCallback(async () => {
    if (!orderId) {
      setError(t("shared.status.notFound", { ns: "common" }));
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setActionLoading(null); // Reset action loading
    try {
      // Use the common getOrderById, backend handles permissions
      const data = await orderService.getOrderById(orderId);
      setOrder(data);
    } catch (err) {
      setError(err.message || tCommon("status.error"));
      console.error(`Fetch order ${orderId} error:`, err);
      document.title = tCommon("status.notFound");
    } finally {
      setLoading(false);
    }
  }, [orderId, t, tCommon]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  // Update document title AFTER order is loaded
  useEffect(() => {
    if (order) {
      document.title = t("user.detail.title", {
        orderNumber: order.orderNumber || orderId,
      });
    } else if (!loading && error) {
      document.title = tCommon("status.error");
    } else if (!loading && !order) {
      document.title = tCommon("status.notFound");
    } else {
      document.title = t("user.list.title"); // Default while loading
    }

    // Cleanup function to reset title
    return () => {
      document.title = "B2B E-Commerce";
    };
  }, [order, orderId, loading, error, t, tCommon]); // Add dependencies

  // --- User Action Handlers ---
  const handleDownloadInvoice = async () => {
    setActionLoading("downloadInvoice");
    try {
      const blob = await orderService.downloadInvoice(orderId, false);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `invoice_${order.orderNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(t("user.detail.actions.invoiceDownloaded", "Invoice downloaded successfully"));
    } catch (err) {
      const errMsg =
        err.response?.data?.message || err.message || tCommon("status.error");
      toast.error(errMsg);
    } finally {
      setActionLoading(null);
    }
  };

  const handleConfirmDelivery = async () => {
    setActionLoading("confirmDelivery");
    setError(null);
    try {
      const updatedOrder = await orderService.confirmDelivery(orderId);
      setOrder(updatedOrder); // Update local state
      toast.success(tCommon("status.success"));
    } catch (err) {
      const errMsg =
        err.response?.data?.message || err.message || tCommon("status.error");
      setError(errMsg);
      toast.error(errMsg);
    } finally {
      setActionLoading(null);
    }
  };

  const handleOpenReturnDialog = () => {
    setReturnReason(""); // Reset reason
    setReturnType("full"); // Reset to full return by default
    setSelectedItems([]); // Reset selected items
    setItemQuantities({}); // Reset quantities
    setItemReasons({}); // Reset item-specific reasons
    setValidationError(""); // Reset validation errors
    setReturnDialogOpen(true);
  };

  // Handle change in return type selection
  const handleReturnTypeChange = (event) => {
    setReturnType(event.target.value);
    // Reset selections when type changes
    setSelectedItems([]);
    setItemQuantities({});
  };

  // Handle item selection toggle
  const handleItemToggle = (itemId) => {
    const currentIndex = selectedItems.indexOf(itemId);
    const newSelectedItems = [...selectedItems];

    if (currentIndex === -1) {
      // Item is being selected, add it and initialize its quantity to 1
      newSelectedItems.push(itemId);
      setItemQuantities((prev) => ({
        ...prev,
        [itemId]: 1, // Default to 1
      }));
    } else {
      // Item is being deselected, remove it and its quantity
      newSelectedItems.splice(currentIndex, 1);
      setItemQuantities((prev) => {
        const updated = { ...prev };
        delete updated[itemId];
        return updated;
      });
      // Also remove any item-specific reason
      setItemReasons((prev) => {
        const updated = { ...prev };
        delete updated[itemId];
        return updated;
      });
    }

    setSelectedItems(newSelectedItems);
  };

  // Handle quantity change for an item
  const handleQuantityChange = (itemId, quantity, maxQuantity) => {
    // Ensure quantity is between 1 and maxQuantity
    const validQuantity = Math.min(Math.max(1, quantity), maxQuantity);
    setItemQuantities((prev) => ({
      ...prev,
      [itemId]: validQuantity,
    }));
  };

  // Handle item-specific reason change
  const handleItemReasonChange = (itemId, reason) => {
    setItemReasons((prev) => ({
      ...prev,
      [itemId]: reason,
    }));
  };

  const handleRequestReturn = async () => {
    // Validate input
    if (!returnReason.trim()) {
      setValidationError(t("user.detail.return.provideReason"));
      return;
    }

    if (returnType === "partial" && selectedItems.length === 0) {
      setValidationError(t("user.detail.return.selectAtLeastOne"));
      return;
    }

    // Prepare the request payload
    const payload = {
      reason: returnReason,
      items: [],
    };

    if (returnType === "full") {
      // For full return, include all order items
      payload.items = order.items.map((item) => ({
        orderItemId: item.id,
        quantity: item.quantity,
        reason: returnReason,
      }));
    } else {
      // For partial return, include only selected items with specified quantities
      payload.items = selectedItems.map((itemId) => {
        const orderItem = order.items.find((item) => item.id === itemId);
        return {
          orderItemId: itemId,
          quantity: itemQuantities[itemId],
          reason: itemReasons[itemId] || returnReason, // Use item-specific reason or fall back to general reason
        };
      });
    }

    setActionLoading("requestReturn");
    setError(null);
    setReturnDialogOpen(false);
    try {
      const updatedOrder = await orderService.requestReturn(
        orderId,
        returnReason,
        payload.items
      );
      setOrder(updatedOrder); // Update local state
      toast.success(t("user.detail.return.successMessage"));
    } catch (err) {
      const errMsg =
        err.response?.data?.message || err.message || tCommon("status.error");
      setError(errMsg);
      toast.error(errMsg);
    } finally {
      setActionLoading(null);
    }
  };

  // Add new handler for user cancel
  const handleUserCancelOrder = async () => {
    // Optional: Add a confirmation dialog here for safety
    const confirmCancel = window.confirm(
      t("admin.detail.dialog.cancelOrder.message", {
        orderNumber: order.orderNumber,
      })
    );
    if (!confirmCancel) return;

    setActionLoading("cancelOrderUser");
    setError(null);
    try {
      const updatedOrder = await orderService.cancelOrderUser(orderId);
      setOrder(updatedOrder); // Update local state
      toast.success(tCommon("status.success"));
    } catch (err) {
      const errMsg =
        err.response?.data?.message || err.message || tCommon("status.error");
      setError(errMsg);
      toast.error(errMsg);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <Container
        maxWidth="md"
        sx={{ mt: 4, mb: 4, display: "flex", justifyContent: "center" }}
      >
        <CircularProgress />
      </Container>
    );
  }

  if (error && !actionLoading) {
    // Show fetch error only if not loading action
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
        <Button component={RouterLink} to="/orders">
          {t("user.detail.backToOrders")}
        </Button>
      </Container>
    );
  }

  if (!order) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Typography>{tCommon("status.notFound")}</Typography>
        <Button component={RouterLink} to="/orders">
          {t("user.detail.backToOrders")}
        </Button>
      </Container>
    );
  }

  // Extract necessary data
  const {
    orderNumber,
    createdAt,
    status,
    paymentStatus,
    total,
    shippingAddress,
    shippingCity,
    shippingPostalCode,
    shippingCountry,
    billingAddress,
    billingCity,
    billingPostalCode,
    billingCountry,
    items = [],
    paymentMethod,
    paymentInstructions, // This holds the manual bank transfer details
  } = order;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1">
          {t("user.detail.title", { orderNumber })}
        </Typography>
        <Button component={RouterLink} to="/orders" variant="outlined">
          {t("user.detail.backToOrders")}
        </Button>
      </Box>

      {/* Display Action Error if occurred */}
      {error && actionLoading && (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Order Summary & Status */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: "100%" }}>
            <Typography variant="h6" gutterBottom>
              {t("user.detail.orderSummary")}
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText
                  primary={t("shared.orderDetails.orderNumber")}
                  secondary={orderNumber}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={t("shared.orderDetails.orderDate")}
                  secondary={format(new Date(createdAt), "PPpp")}
                />
              </ListItem>
              <ListItem>
                <ListItemText primary={t("shared.orderDetails.orderStatus")} />
                <Chip
                  label={t(
                    `shared.status.${status?.toLowerCase()}`,
                    status || t("shared.status.unknown")
                  )}
                  color={getStatusChipColor(status)}
                  size="small"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={t("shared.orderDetails.paymentStatus")}
                />
                <Chip
                  label={t(
                    `shared.status.${paymentStatus?.toLowerCase()}`,
                    paymentStatus || t("shared.status.unknown")
                  )}
                  color={getStatusChipColor(paymentStatus)}
                  size="small"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={t("shared.orderDetails.paymentMethod")}
                  secondary={paymentMethod || "N/A"}
                />
              </ListItem>
              {order.amountPaidByWallet > 0 && (
                <ListItem>
                  <ListItemText
                    primary={t("shared.orderDetails.walletPayment")}
                    secondary={`${parseFloat(order.amountPaidByWallet).toFixed(
                      2
                    )} CHF`}
                  />
                </ListItem>
              )}
              {order.amountPaidByWallet > 0 && paymentMethod !== "wallet" && (
                <ListItem>
                  <ListItemText
                    primary={
                      paymentMethod === "credit_card"
                        ? t("shared.orderDetails.cardPayment")
                        : paymentMethod === "bank_transfer_manual"
                        ? t("shared.orderDetails.bankTransferPayment")
                        : paymentMethod
                    }
                    secondary={`${(
                      parseFloat(total) - parseFloat(order.amountPaidByWallet)
                    ).toFixed(2)} CHF`}
                  />
                </ListItem>
              )}
              <ListItem>
                <ListItemText
                  primary={t("shared.orderDetails.totalAmount")}
                  secondary={`${
                    typeof total === "number" ||
                    (typeof total === "string" && !isNaN(parseFloat(total)))
                      ? parseFloat(total).toFixed(2)
                      : "N/A"
                  } CHF`}
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>

        {/* Shipping & Billing Addresses */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: "100%" }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="h6" gutterBottom>
                  {t("shared.address.shipping")}
                </Typography>
                <Typography variant="body2">{shippingAddress}</Typography>
                <Typography variant="body2">
                  {shippingPostalCode} {shippingCity}
                </Typography>
                <Typography variant="body2">{shippingCountry}</Typography>

                {/* Add shipping method information */}
                <Box sx={{ mt: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    {t("shared.orderDetails.shippingMethod", "Shipping Method")}
                  </Typography>
                  <Typography variant="body2">
                    {order.shippingMethod?.name ||
                      order.shippingMethodName ||
                      "Standard Shipping"}
                  </Typography>

                  {order.shippingMethod?.estimatedDeliveryTime && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>
                        {t(
                          "shared.orderDetails.estimatedDelivery",
                          "Est. Delivery"
                        )}
                        :
                      </strong>{" "}
                      {order.shippingMethod.estimatedDeliveryTime}
                    </Typography>
                  )}

                  {order.status === "shipped" && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2">
                        <strong>
                          {t(
                            "shared.orderDetails.trackingNumber",
                            "Tracking Number"
                          )}
                          :
                        </strong>{" "}
                        {order.metadata?.trackingNumber ||
                          t(
                            "shared.orderDetails.notAvailable",
                            "Not available"
                          )}
                      </Typography>

                      {order.metadata?.trackingNumber &&
                        order.shippingMethod?.trackingUrlFormat && (
                          <Button
                            variant="outlined"
                            size="small"
                            component="a"
                            href={order.shippingMethod.trackingUrlFormat.replace(
                              "{trackingNumber}",
                              order.metadata.trackingNumber
                            )}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ mt: 1 }}
                          >
                            {t(
                              "shared.orderDetails.trackPackage",
                              "Track Package"
                            )}
                          </Button>
                        )}

                      {order.metadata?.trackingNumber &&
                        !order.shippingMethod?.trackingUrlFormat && (
                          <Button
                            variant="outlined"
                            size="small"
                            component="a"
                            href={`https://track.yourpackage.com/${order.metadata.trackingNumber}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ mt: 1 }}
                          >
                            {t(
                              "shared.orderDetails.trackPackage",
                              "Track Package"
                            )}
                          </Button>
                        )}
                    </Box>
                  )}
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="h6" gutterBottom>
                  {t("shared.address.billing")}
                </Typography>
                <Typography variant="body2">{billingAddress}</Typography>
                <Typography variant="body2">
                  {billingPostalCode} {billingCity}
                </Typography>
                <Typography variant="body2">{billingCountry}</Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* User Actions Area */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t("user.detail.actions.title")}
            </Typography>
            <Box sx={{ display: "flex", gap: 2, mt: 1, flexWrap: "wrap" }}>
              {/* Download Invoice Button */}
              <Button
                variant="outlined"
                color="primary"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadInvoice}
                disabled={actionLoading === "downloadInvoice"}
              >
                {actionLoading === "downloadInvoice" ? (
                  <CircularProgress size={24} />
                ) : (
                  t("user.detail.actions.downloadInvoice", "Download Invoice")
                )}
              </Button>

              {order.status === "shipped" && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircleOutlineIcon />}
                  onClick={handleConfirmDelivery}
                  disabled={actionLoading === "confirmDelivery"}
                >
                  {actionLoading === "confirmDelivery" ? (
                    <CircularProgress size={24} />
                  ) : (
                    t("user.detail.actions.confirmDelivery")
                  )}
                </Button>
              )}
              {order.status === "delivered" && (
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<AssignmentReturnIcon />}
                  onClick={handleOpenReturnDialog}
                  disabled={actionLoading === "requestReturn"}
                >
                  {t("user.detail.actions.requestReturn")}
                </Button>
              )}
              {order.status === "awaiting_payment" &&
                order.paymentMethod === "bank_transfer_manual" && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<CancelIcon />}
                    onClick={handleUserCancelOrder}
                    disabled={actionLoading === "cancelOrderUser"}
                  >
                    {actionLoading === "cancelOrderUser" ? (
                      <CircularProgress size={24} />
                    ) : (
                      t("user.detail.actions.cancelOrder")
                    )}
                  </Button>
                )}
            </Box>
          </Paper>
        </Grid>

        {/* Payment Instructions (if Manual Bank Transfer) */}
        {paymentMethod === "bank_transfer_manual" &&
          paymentInstructions &&
          order.status === "awaiting_payment" && (
            <Grid item xs={12}>
              <Paper
                variant="outlined"
                sx={{ p: 2, backgroundColor: "grey.50" }}
              >
                <Typography variant="h6" gutterBottom>
                  {t("user.detail.paymentInstructions.title")}
                </Typography>
                <Typography variant="body2" paragraph>
                  {t("user.detail.paymentInstructions.description")}
                </Typography>
                <ManualBankTransferInstructions
                  instructions={paymentInstructions}
                />
                {/* Consider adding a copy button for IBAN etc. */}
              </Paper>
            </Grid>
          )}

        {/* Order Items */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t("shared.orderDetails.items")}
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("shared.orderDetails.product")}</TableCell>
                    <TableCell>{t("shared.orderDetails.sku")}</TableCell>
                    <TableCell align="right">
                      {t("shared.orderDetails.price")}
                    </TableCell>
                    <TableCell align="center">
                      {t("shared.orderDetails.quantity")}
                    </TableCell>
                    <TableCell align="right">
                      {t("shared.orderDetails.subtotal")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Link
                          component={RouterLink}
                          to={`/products/${
                            item.product?.parentId
                              ? item.product.parentId
                              : item.productId
                          }`}
                        >
                          {item.product?.name || tCommon("status.notFound")}
                        </Link>
                        {item.product?.sku && (
                          <Typography variant="caption" display="block">
                            SKU: {item.product.sku}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{item.product?.sku || "N/A"}</TableCell>
                      <TableCell align="right">
                        {`${
                          typeof item.price === "number" ||
                          (typeof item.price === "string" &&
                            !isNaN(parseFloat(item.price)))
                            ? parseFloat(item.price).toFixed(2)
                            : "N/A"
                        } CHF`}
                      </TableCell>
                      <TableCell align="center">{item.quantity}</TableCell>
                      <TableCell align="right">
                        {`${
                          typeof item.price === "number" ||
                          (typeof item.price === "string" &&
                            !isNaN(parseFloat(item.price)))
                            ? (parseFloat(item.price) * item.quantity).toFixed(
                                2
                              )
                            : "N/A"
                        } CHF`}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Return Request Dialog */}
      <Dialog
        open={returnDialogOpen}
        onClose={() => setReturnDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t("user.detail.return.dialogTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t("user.detail.return.dialogDescription", { orderNumber })}
          </DialogContentText>

          {/* Return Type Selection */}
          <FormControl component="fieldset" sx={{ mb: 2, mt: 1 }}>
            <FormLabel component="legend">
              {t("user.detail.return.returnType")}
            </FormLabel>
            <RadioGroup
              name="returnType"
              value={returnType}
              onChange={handleReturnTypeChange}
              row
            >
              <FormControlLabel
                value="full"
                control={<Radio />}
                label={t("user.detail.return.fullReturn")}
              />
              <FormControlLabel
                value="partial"
                control={<Radio />}
                label={t("user.detail.return.partialReturn")}
              />
            </RadioGroup>
          </FormControl>

          {/* General Return Reason */}
          <TextField
            margin="dense"
            id="returnReason"
            label={t("user.detail.return.generalReason")}
            type="text"
            fullWidth
            multiline
            rows={2}
            variant="outlined"
            value={returnReason}
            onChange={(e) => setReturnReason(e.target.value)}
            required
            error={Boolean(validationError && !returnReason.trim())}
            helperText={
              validationError && !returnReason.trim()
                ? t("user.detail.return.provideReason")
                : ""
            }
          />

          {/* Partial Return Items Selection */}
          {returnType === "partial" && order?.items?.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                {t("user.detail.return.selectItems")}
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox"></TableCell>
                      <TableCell>{t("shared.orderDetails.product")}</TableCell>
                      <TableCell align="right">
                        {t("shared.orderDetails.price")}
                      </TableCell>
                      <TableCell align="center">
                        {t("user.detail.return.orderedQty")}
                      </TableCell>
                      <TableCell align="center">
                        {t("user.detail.return.returnQty")}
                      </TableCell>
                      <TableCell>
                        {t("user.detail.return.itemSpecificReason")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {order.items.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedItems.indexOf(item.id) !== -1}
                            onChange={() => handleItemToggle(item.id)}
                          />
                        </TableCell>
                        <TableCell>
                          {item.product?.name || tCommon("status.notFound")}
                        </TableCell>
                        <TableCell align="right">
                          {parseFloat(item.price).toFixed(2)} CHF
                        </TableCell>
                        <TableCell align="center">{item.quantity}</TableCell>
                        <TableCell align="center">
                          <TextField
                            type="number"
                            size="small"
                            inputProps={{
                              min: 1,
                              max: item.quantity,
                              step: 1,
                            }}
                            value={
                              selectedItems.indexOf(item.id) !== -1
                                ? itemQuantities[item.id]
                                : ""
                            }
                            onChange={(e) =>
                              handleQuantityChange(
                                item.id,
                                parseInt(e.target.value),
                                item.quantity
                              )
                            }
                            disabled={selectedItems.indexOf(item.id) === -1}
                            sx={{ width: "80px" }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            placeholder={t(
                              "user.detail.return.optionalItemReason"
                            )}
                            size="small"
                            fullWidth
                            value={itemReasons[item.id] || ""}
                            onChange={(e) =>
                              handleItemReasonChange(item.id, e.target.value)
                            }
                            disabled={selectedItems.indexOf(item.id) === -1}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {validationError && selectedItems.length === 0 && (
                <FormHelperText error>
                  {t("user.detail.return.selectAtLeastOne")}
                </FormHelperText>
              )}
            </Box>
          )}

          {validationError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {validationError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setReturnDialogOpen(false)}
            disabled={!!actionLoading}
          >
            {tCommon("buttons.cancel")}
          </Button>
          <Button
            onClick={handleRequestReturn}
            color="primary"
            disabled={
              !returnReason.trim() ||
              (returnType === "partial" && selectedItems.length === 0) ||
              !!actionLoading
            }
          >
            {actionLoading === "requestReturn" ? (
              <CircularProgress size={24} />
            ) : (
              t("user.detail.return.submitRequest")
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default OrderDetailPage;
